import { Country } from '@afterpay/types'
import { formatPercentage, valueIsNotNullOrUndefined } from '@afterpay/utils'
import { useRouter } from 'next/router'
import { SupportedLocale } from '../../model'
import { useCheckoutState, useCountry, useCreditCheckConsented, useGetTermsDisclosureStatus, useUkRegFlags } from '../../state'
import { TermsDisclosureStatus } from '@afterpaytouch/portal-api'
import { useMoney } from '../Money/useMoney'
import { selectTermsKey } from './selectTermsKey'
import { useTermsContext } from './termsContext'

interface LateFeeDetails {
  isZeroLateFee: boolean
  lateFee: string
}

interface TermsEventProps {
  ppa: string
  lateFee: boolean
  creditCheckConsent: boolean
  creditCheckConsented?: boolean
  termsDisclosureAgree?: boolean
  accountOpeningDisclosures?: boolean
}

export const useLateFeeDetails = (): LateFeeDetails => {
  const { started } = useCheckoutState()
  const locale = useRouter().locale as SupportedLocale
  const lateFeeCurrency = started?.firstLateFeeData?.fixedAmount?.currency ?? started?.consumerAmount.currency
  const { formatMoney } = useMoney({ locale, currency: lateFeeCurrency, decimalPlaces: 2 })

  const firstLateFee = started?.firstLateFeeData
  const zeroLateFee = valueIsNotNullOrUndefined(firstLateFee) ? firstLateFee.zeroLateFee : true
  const fixedAmount = firstLateFee?.fixedAmount?.amount ?? '0'
  const lateFeePercentage = firstLateFee?.variable ?? '0'
  const showFixedAmount = firstLateFee?.amountInCheckout ?? false

  const lateFee = showFixedAmount ? formatMoney(fixedAmount) : formatPercentage(lateFeePercentage)
  return {
    isZeroLateFee: zeroLateFee,
    lateFee,
  }
}

export const useTermsEventProps = (): TermsEventProps => {
  const terms = useGetTerms()
  const creditCheckConsent = terms.toLowerCase().includes('creditcheckconsent')
  const creditCheckConsented = useCreditCheckConsented()
  const termsDisclosureAgree = useGetTermsDisclosureStatus() === TermsDisclosureStatus.NEW_USER_NOT_ACCEPTED_TERMS
  return {
    ppa: terms.includes('estimatedPPA') ? 'estimated' : 'standard',
    lateFee: terms.includes('lateFee'),
    creditCheckConsent,
    creditCheckConsented,
    termsDisclosureAgree: termsDisclosureAgree || undefined,
  }
}

export const useIsUkRegulatedTerms = (): boolean => {
  const { isUkRegulationEnabled } = useUkRegFlags()
  return isUkRegulationEnabled
}

export const useShowTermsDisclosure = (): boolean => {
  const country = useCountry()
  const termsDisclosureStatus = useGetTermsDisclosureStatus()
  return termsDisclosureStatus === TermsDisclosureStatus.NEW_USER_NOT_ACCEPTED_TERMS && country === Country.NZ
}

export const useGetTerms = (): string => selectTermsKey(useTermsContext())
